import React, {useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import Icon from '@/shared/components/Icon';
import UserService from '@/shared/services/userService';
import {useDashboardModules} from '@/modules/patient-dashboard/hooks/useDashboardModules';
import PointsRewardsModal from '@/modules/patient-dashboard/components/PointsRewardsModal';

interface Props {
    isSubscribed: boolean;
}

/**
 * Header element (per Nathan's v6 mockup): a "My Labs+ Member" status badge +
 * a points pill that opens the Points & Rewards modal. Points come from Sean's
 * #46 engine via UserService.getPoints(); the query key matches the modal's so
 * they dedupe. Hidden on xs (matches the account name's `hidden sm:block`).
 *
 * The points pill is switched per organization (Derrick, 2026-08-13) by the
 * "Points & Rewards" module in Super Admin → Organizations → Modules, so which
 * tenants offer points is an admin decision rather than a rule in the code.
 *
 * NOTE the deliberate inversion of the usual module default: `useDashboardModules`
 * returns null until the config loads and callers normally read that as "show
 * everything", which is right for a dashboard card (never blank the page) but
 * wrong here — it would flash the pill on every load for every org that has
 * points off. An optional header badge is better hidden until we know it
 * belongs, so this reads the set directly instead of useIsDashboardModuleEnabled.
 */
const HeaderMembershipPoints: React.FC<Props> = ({isSubscribed}) => {
    const [modalOpen, setModalOpen] = useState(false);
    const enabledModules = useDashboardModules();
    const pointsEnabled = enabledModules?.has('patient.points') === true;
    const {data: pointsData} = useQuery({
        queryKey: ['user', 'points'],
        queryFn: () => UserService.getPoints(),
        staleTime: 5 * 60 * 1000,
        enabled: pointsEnabled,
    });
    const totalPoints = pointsData?.totalPoints ?? 0;

    if (!isSubscribed && !pointsEnabled) return null;

    return (
        <>
            <div className="hidden sm:flex items-center gap-2">
                {isSubscribed && (
                    <span className="inline-flex items-center rounded-md border border-mld-primary/30 bg-mld-primary/15 px-3 py-1 text-[12px] font-medium text-mld-text-primary">
                        My Labs+ Member
                    </span>
                )}
                {pointsEnabled && (
                    <button
                        type="button"
                        onClick={() => setModalOpen(true)}
                        className="inline-flex items-center gap-1.5 rounded-md border border-mld-panel-border px-3 py-1 text-[12px] font-medium text-mld-text-primary hover:border-mld-primary transition-colors">
                        <Icon name="star" className="h-3.5 w-3.5 text-mld-primary" />
                        {totalPoints} Points
                    </button>
                )}
            </div>
            {modalOpen && <PointsRewardsModal onClose={() => setModalOpen(false)} />}
        </>
    );
};

export default HeaderMembershipPoints;
